import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { useAdmin } from "../AdminProvider";
import { modalVariants, childVariants, cld } from "shared/utils";
import "../styles/EditCertificatesModal.css";
const resolveImg = (val, width) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return cld(val, width);
};
const EditAboutModal = ({
  isOpen,
  onClose,
  onSave,
  aboutData,
  publishedData,
}) => {
  const { aboutDraft, saveAboutDraft } = useAdmin();
  const [formData, setFormData] = useState({
    description: "",
    imageUrl: "",
    resumeUrl: "",
    completedProjects: 0,
    yearsExperience: 0,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [, setImageFile] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Modal is opening - load current data (draft or published)
      const displayData = aboutDraft || publishedData || {};
      setFormData({
        description: displayData.description || "",
        imageUrl: displayData.imageUrl || "",
        resumeUrl: displayData.resumeUrl || "",
        completedProjects: displayData.completedProjects || 0,
        yearsExperience: displayData.yearsExperience || 0,
      });
      setErrors({});
    } else {
      // Modal is closing - reset form
      setFormData({
        description: "",
        imageUrl: "",
        resumeUrl: "",
        completedProjects: 0,
        yearsExperience: 0,
      });
      setErrors({});
    }
  }, [isOpen, aboutDraft, publishedData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = "Image URL is required";
    }
    if (!formData.resumeUrl.trim()) {
      newErrors.resumeUrl = "Resume URL is required";
    }
    if (formData.completedProjects < 0) {
      newErrors.completedProjects = "Completed projects cannot be negative";
    }
    if (formData.yearsExperience < 0) {
      newErrors.yearsExperience = "Years of experience cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const updatedAbout = {
        description: formData.description.trim(),
        imageUrl: formData.imageUrl.trim(),
        resumeUrl: formData.resumeUrl.trim(),
        completedProjects: parseInt(formData.completedProjects) || 0,
        yearsExperience: parseInt(formData.yearsExperience) || 0,
      };

      await saveAboutDraft(updatedAbout);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving about:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, imageUrl: previewUrl });
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              Edit About Section
            </motion.h3>

            <div className="preview-modal-container">
              {/* Left Side: Form Inputs */}
              <motion.div
                style={{ flex: 1 }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Description Input */}
                <motion.div
                  style={{ position: "relative" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Description *
                  </label>
                  <textarea
                    className="glass__card"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter your professional description..."
                    style={{
                      marginTop: "0.5rem",
                      width: "100%",
                      minHeight: "4rem",
                      padding: "0.75rem 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                      resize: "vertical",
                      height: "125px",
                    }}
                  />
                  {errors.description && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.description}
                    </span>
                  )}
                </motion.div>

                {/* Resume URL Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Resume URL *
                  </label>
                  <input
                    type="url"
                    className="glass__card"
                    name="resumeUrl"
                    value={formData.resumeUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/resume.pdf"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.resumeUrl && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.resumeUrl}
                    </span>
                  )}
                </motion.div>

                {/* Completed Projects Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Completed Projects *
                  </label>
                  <input
                    className="glass__card"
                    type="number"
                    name="completedProjects"
                    value={formData.completedProjects}
                    onChange={handleInputChange}
                    min="0"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.completedProjects && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.completedProjects}
                    </span>
                  )}
                </motion.div>

                {/* Years of Experience Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Years of Experience *
                  </label>
                  <input
                    className="glass__card"
                    type="number"
                    name="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      marginTop: "0.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.yearsExperience && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.yearsExperience}
                    </span>
                  )}
                </motion.div>
              </motion.div>

              {/* Right Side: Image Preview and URL */}
              <motion.div
                className="image-upload-section"
                style={{
                  flex: "0.5 1 0%",
                  marginTop: "1.5rem",
                  gap: "0.5rem",
                  display: "flex",
                  flexDirection: "column",
                }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Image Preview */}
                <label
                  className="glass__card"
                  style={{
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    width: "100%",
                    aspectRatio: "3 / 3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                    position: "relative",
                    padding: "0.5rem",
                    border: "2px solid var(--title-color)",
                    cursor: "pointer",
                  }}
                >
                  {formData.imageUrl ? (
                    <>
                      <img
                        src={resolveImg(
                          formData?.imageUrl || formData?.imageUrl,
                          700,
                        )}
                        alt="Profile Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "0.5rem",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          width: "2.5rem",
                          height: "2.5rem",
                          borderRadius: "0.375rem",
                          backgroundColor: "var(--title-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        <i
                          className="bx bx-upload"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--container-color)",
                          }}
                        ></i>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-color)",
                        textAlign: "center",
                        padding: "1rem",
                      }}
                    >
                      <i
                        className="bx bx-upload"
                        style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
                      ></i>
                      <span style={{ fontSize: "0.75rem" }}>
                        Upload Profile Image
                      </span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </label>

                {/* Image URL Input */}
                <div style={{ position: "relative" }}>
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      fontWeight: "600",
                      fontSize: "small",
                      marginTop: "1rem",
                    }}
                  >
                    Image URL *
                  </label>
                  <input
                    type="url"
                    className="glass__card"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      padding: "0 1rem",
                      marginTop: "0.5rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                    }}
                    onClick={() =>
                      formData.imageUrl &&
                      navigator.clipboard.writeText(formData.imageUrl)
                    }
                    title={
                      formData.imageUrl ? "Click to copy URL" : "Paste URL here"
                    }
                  />
                  {errors.imageUrl && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.imageUrl}
                    </span>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <motion.div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle="Save Changes"
                buttonText={saving ? "Saving..." : "Save Changes"}
                onClick={handleSave}
                disabled={saving}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditAboutModal;
